"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Navbar = void 0;
const Block_1 = require("./Block");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const ui_1 = require("@pixi/ui");
const play_svg_1 = __importDefault(require("../../assets/svg/play.svg"));
const palette_svg_1 = __importDefault(require("../../assets/svg/palette.svg"));
const podium_svg_1 = __importDefault(require("../../assets/svg/podium.svg"));
const Global_1 = require("../Global");
const NavButton_1 = require("./NavButton");
const settings_1 = require("../config/settings");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class Navbar extends Block_1.Block {
    constructor(app, gameManager, sceneManager) {
        super(app);
        this.gameManager = gameManager;
        this.sceneManager = sceneManager;
        this.border = 1;
        this.maxWidth = 400;
        this.fontSize = 22;
        this.alphaFilter = new pixi_js_1.AlphaFilter(0.4);
        this._enabled = true;
        this.name = 'navbar';
        this.zIndex = SceneManager_1.Layer.Bottom;
        this.textStyle = Block_1.Block.textStyle(this.fontSize);
        this.textStyle.align = 'center';
        this.background = this.addChild(this.makeBackground());
        this.accent = this.addChild(this.makeAccent());
        this.accent.filters = [this.alphaFilter];
        this.buttons = new pixi_js_1.Container();
        this.play = (new NavButton_1.NavButton(app, gameManager, play_svg_1.default, 0.23, 'mainmenu.home', this.textStyle)).asButton();
        this.sigs.push(this.play.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Home);
            AssetManager_1.default.stickSound();
        })));
        this.atelier = (new NavButton_1.NavButton(app, gameManager, palette_svg_1.default, 0.14, 'mainmenu.atelier', this.textStyle)).asButton();
        this.sigs.push(this.atelier.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Atelier);
            AssetManager_1.default.squishSound();
        })));
        this.stats = (new NavButton_1.NavButton(app, gameManager, podium_svg_1.default, 0.11, 'mainmenu.stats', this.textStyle)).asButton();
        this.sigs.push(this.stats.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Stats);
            AssetManager_1.default.slideSound();
        })));
        this.buttons.addChild(this.play, this.atelier, this.stats);
        this.addChild(this.buttons);
        this.subs.add(this.sceneManager.sceneWillChange$.subscribe((coming) => {
            let bump = (this.play.getChildAt(0));
            if ((coming === null || coming === void 0 ? void 0 : coming.name) && this[coming.name]) {
                bump = (this[coming.name].getChildAt(0));
            }
            this.animate(bump);
        }));
    }
    set enabled(enabled) {
        this._enabled = enabled;
        this.layout();
    }
    get enabled() {
        return this._enabled;
    }
    animate(nav) {
        this.buttons.children.forEach((b) => {
            (b.getChildAt(0)).timeline.reverse();
        });
        nav.timeline.play();
    }
    get height() {
        return this.background.height;
    }
    get width() {
        return this.background.width;
    }
    makeBackground() {
        const landscape = this.isLandscapeBox;
        return this.makeBox({
            backgroundColor: settings_1.Settings.statsColor,
            width: landscape ? settings_1.Settings.navHeight : this.app.screen.width + 2,
            height: landscape ? this.app.screen.height : settings_1.Settings.navHeight,
            border: this.border,
            borderColor: settings_1.Settings.blackColor,
            radius: 0
        }, this.background);
    }
    makeAccent(factor = 0) {
        const landscape = this.isLandscapeBox;
        return this.makeBox({
            backgroundColor: settings_1.Settings.whiteColor,
            width: landscape ? this.background.width / (0, Global_1.px)() - this.border * 2 : factor / (0, Global_1.px)(),
            height: landscape ? factor / (0, Global_1.px)() : this.background.height / (0, Global_1.px)() - this.border * 2,
            border: 0,
            radius: 0,
        }, this.accent);
    }
    get active() {
        var _a;
        let active = this.buttons.getChildAt(0) || this.play;
        const name = (_a = this.sceneManager.currentScene) === null || _a === void 0 ? void 0 : _a.name;
        if (name && this[name] instanceof ui_1.ButtonContainer) {
            active = this[name];
        }
        return active;
    }
    destroy(_options) {
        super.destroy(_options);
        this.alphaFilter.destroy();
    }
    layout() {
        var _a, _b;
        super.layout();
        this.makeBackground().position.x = (0, Global_1.px)(-this.border);
        this.textStyle.fontSize = (0, Global_1.fontSize)(this.fontSize);
        this.textStyle.fontFamily = (0, Global_1.font)();
        const landscape = this.isLandscapeBox;
        const factor = Math.min(landscape ? this.app.screen.height
            : this.app.screen.width, (0, Global_1.px)(this.maxWidth), (0, Global_1.px)(settings_1.Settings.maxWidth));
        const perbutton = factor / this.buttons.children.length;
        this.buttons.children.forEach((b, i) => {
            const nb = (b.button.view.getChildAt(0));
            if (landscape) {
                nb.setSize(settings_1.Settings.navHeight, perbutton / (0, Global_1.px)());
                b.position.set(-(0, Global_1.px)(this.border), perbutton * i);
            }
            else {
                nb.setSize(perbutton / (0, Global_1.px)(), settings_1.Settings.navHeight);
                b.position.set(perbutton * ((0, Global_1.isrtl)() ? this.buttons.children.length - 1 - i : i), (0, Global_1.px)(this.border));
            }
            b.enabled = this._enabled;
        });
        if (landscape) {
            this.buttons.position.set(0, this.center.y - this.buttons.height / 2);
        }
        else {
            this.buttons.position.set(this.center.x - this.buttons.width / 2, 0);
        }
        // Highlight active
        const ac = this.makeAccent((landscape ? (_a = this.active) === null || _a === void 0 ? void 0 : _a.height : (_b = this.active) === null || _b === void 0 ? void 0 : _b.width) || perbutton);
        if (landscape) {
            ac.position.set((0, Global_1.px)(-this.border), this.buttons.position.y + this.active.position.y);
        }
        else {
            ac.position.set(this.buttons.position.x + this.active.position.x, (0, Global_1.px)(this.border));
        }
        this.active.enabled = false;
    }
}
exports.Navbar = Navbar;
